/**
 * «Empezar de una anterior» (29/10/2026), en Nueva campaña: las últimas campañas del WhatsApp oficial
 * (`anterioresParaEmpezar`), con su nombre, su estado, cuándo salió y a quién. Elegir una la duplica, igual que el
 * «Duplicar» de la lista y del detalle (`useDuplicarCampana`), y abre la copia en el asistente: el paso lo resuelve lo
 * guardado, y con el horario y la baja de la original llega a «Revisar y mandar» (cuándo sale y la prueba se eligen ahí).
 *
 * Sólo del equipo, como el asistente: a una persona de un cliente no se le muestra (el backend le niega duplicar con 403
 * SOLO_EQUIPO). Sin campañas anteriores, lo dice.
 */

import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { oficialApi } from "../api";
import type { CampanaResumen } from "../types";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { anterioresParaEmpezar, lineaDeLaAnterior } from "../logic/asistente";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { ESTADO_CAMPANA } from "../logic/textos";
import { useDuplicarCampana } from "./AccionesCampana";

export const TEXTO_EMPEZAR_DE_UNA_ANTERIOR =
  "Conviene si ya mandaste una parecida por el WhatsApp oficial: se copian la plantilla, a quién, las variables, el horario y la baja, y antes de mandar elegís cuándo sale y la probás de nuevo.";
export const TEXTO_SIN_ANTERIORES = "Todavía no hay campañas del WhatsApp oficial para copiar: cuando salga la primera, vas a poder empezar de ella acá.";

export function EmpezarDeUnaAnterior() {
  const equipo = useEsEquipoDeenex();
  const [anteriores, setAnteriores] = useState<CampanaResumen[] | null>(null);
  const [errorLista, setErrorLista] = useState("");
  const { duplicar, duplicando, error } = useDuplicarCampana();

  const cargar = useCallback(async () => {
    setErrorLista("");
    try {
      const l = leerRespuesta<CampanaResumen[]>(await oficialApi.campanas());
      if (!l.ok) { setErrorLista(l.error || textoError(l)); return; }
      setAnteriores(anterioresParaEmpezar(Array.isArray(l.data) ? l.data : []));
    } catch (e) {
      setErrorLista(textoError(leerError(e)));
    }
  }, []);
  useEffect(() => {
    if (equipo) void cargar();
  }, [equipo, cargar]);

  if (!equipo) return null;
  return (
    <section aria-labelledby="titulo-empezar-de-una-anterior" className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="space-y-1">
        <span className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">WhatsApp oficial</span>
        <h2 id="titulo-empezar-de-una-anterior" className="text-base font-semibold text-slate-900">Empezar de una anterior</h2>
        <p className="text-sm font-medium text-slate-800 break-words" data-testid="conviene-anterior">{TEXTO_EMPEZAR_DE_UNA_ANTERIOR}</p>
      </div>
      {errorLista ? (
        <p className="flex flex-wrap items-center gap-x-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          No pudimos traer las campañas anteriores. {errorLista}
          <button type="button" onClick={() => void cargar()} className="inline-flex min-h-[44px] items-center font-semibold underline">Reintentar</button>
        </p>
      ) : anteriores === null ? (
        <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Buscando las campañas anteriores…</p>
      ) : anteriores.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="sin-anteriores">{TEXTO_SIN_ANTERIORES}</p>
      ) : (
        <ul className="space-y-2" aria-label="Campañas anteriores">
          {anteriores.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2">
              <div className="min-w-0 space-y-0.5">
                <p className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900 break-words">{c.nombre}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ESTADO_CAMPANA[c.estado]?.cls || "bg-slate-100 text-slate-600"}`}>{ESTADO_CAMPANA[c.estado]?.label || c.estado}</span>
                </p>
                <p className="text-xs text-slate-500 break-words">{lineaDeLaAnterior(c)}</p>
              </div>
              <button type="button" onClick={() => void duplicar(c.id)} disabled={!!duplicando} aria-label={`Empezar de «${c.nombre}»`}
                className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                {duplicando === c.id ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} aria-hidden="true" />}
                {duplicando === c.id ? "Copiando…" : "Empezar de ésta"}
              </button>
              {error?.id === c.id && <p role="alert" className="w-full text-xs text-red-700">{error.texto}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
